import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { downloadCsv } from "@/lib/api";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import {
    ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { DollarSign, Users, TrendingUp, XCircle, CalendarCheck, Repeat, Download, Eye, MapPin, Globe, Share2, CreditCard } from "lucide-react";

const usd = (n) => `$${Number(n || 0).toLocaleString()}`;
const RANGES = [
    { key: "daily", label: "Daily (30d)" },
    { key: "weekly", label: "Weekly (12w)" },
    { key: "monthly", label: "Monthly (12mo)" },
];

function Kpi({ icon: Icon, label, value, sub, tone = "zinc" }) {
    const tones = {
        zinc: "bg-zinc-900 text-white", emerald: "bg-emerald-600 text-white",
        blue: "bg-blue-600 text-white", red: "bg-red-500 text-white", amber: "bg-amber-500 text-white",
    };
    return (
        <div className="rounded-xl border border-zinc-200 bg-white p-5" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="flex items-center gap-3">
                <span className={`grid h-9 w-9 place-items-center rounded-lg ${tones[tone]}`}><Icon className="h-4 w-4" /></span>
                <p className="text-xs font-600 uppercase tracking-wide text-zinc-500">{label}</p>
            </div>
            <p className="mt-3 text-2xl font-700 tracking-tight text-zinc-900">{value}</p>
            {sub && <p className="mt-0.5 text-xs text-zinc-500">{sub}</p>}
        </div>
    );
}

function ChartCard({ title, subtitle, children }) {
    return (
        <div className="rounded-xl border border-zinc-200 bg-white p-5">
            <p className="text-sm font-600 text-zinc-900">{title}</p>
            {subtitle && <p className="mb-3 text-xs text-zinc-500">{subtitle}</p>}
            <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
            </div>
        </div>
    );
}

export default function Analytics() {
    const [range, setRange] = useState("monthly");
    const { data, isLoading, dataUpdatedAt } = useQuery({ queryKey: ["admin-analytics"], queryFn: async () => (await api.get("/admin/analytics")).data, refetchInterval: 30000, refetchOnWindowFocus: true });
    const { data: traffic } = useQuery({ queryKey: ["admin-visits"], queryFn: async () => (await api.get("/admin/visits/summary")).data, refetchInterval: 30000, refetchOnWindowFocus: true });
    const k = data?.kpis || {};
    const series = data?.[range] || [];
    const updated = dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", second: "2-digit" }) : null;

    return (
        <div>
            <div className="flex items-start justify-between gap-4">
                <AdminHeader title="Analytics & Accounting" subtitle="Income transparency and activity trends. Revenue is computed from active membership tiers (Standard $20/mo, Unlimited $50/mo)." />
                <div className="flex shrink-0 items-center gap-3">
                    {updated && <span className="hidden text-xs text-zinc-400 sm:inline" data-testid="analytics-updated">Live · updated {updated}</span>}
                    <Button variant="outline" size="sm" data-testid="export-analytics-csv" onClick={() => downloadCsv("/admin/export/analytics.csv", "ssc-analytics.csv")}><Download className="mr-1 h-4 w-4" /> Export CSV</Button>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4 lg:grid-cols-3" data-testid="analytics-kpis">
                <Kpi icon={DollarSign} label="MRR" value={usd(k.mrr)} sub={`${usd(k.arr)} annualized`} tone="emerald" />
                <Kpi icon={TrendingUp} label="Est. Monthly Revenue" value={usd(k.est_monthly_revenue)} sub="From active subscriptions" tone="emerald" />
                <Kpi icon={CreditCard} label="Paid Memberships" value={k.paid_memberships ?? "—"} sub="Members who activated a paid plan" tone="emerald" />
                <Kpi icon={Repeat} label="Active Subscriptions" value={k.active_subscriptions ?? "—"} sub={`${k.standard_count || 0} Standard · ${k.unlimited_count || 0} Unlimited`} tone="blue" />
                <Kpi icon={Users} label="Total Members" value={k.total_members ?? "—"} tone="zinc" />
                <Kpi icon={XCircle} label="Cancellations" value={k.canceled_subscriptions ?? "—"} sub="All-time canceled subs" tone="red" />
                <Kpi icon={CalendarCheck} label="Outings Completed" value={k.outings_completed ?? "—"} tone="amber" />
            </div>

            {traffic && (
                <div className="mt-6 rounded-xl border border-zinc-200 bg-white p-5" data-testid="site-traffic">
                    <div className="mb-3 flex items-center gap-2">
                        <Eye className="h-5 w-5 text-zinc-700" />
                        <div>
                            <p className="text-sm font-600 text-zinc-900">Site traffic — landing page visits</p>
                            <p className="text-xs text-zinc-500">{traffic.total} total visits · {traffic.unique} unique visitors · location estimated from visitor IP (last 30 days shown)</p>
                        </div>
                    </div>
                    <div className="h-56 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={traffic.daily} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" interval={4} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                                <Tooltip />
                                <Bar dataKey="visits" fill="#0ea5e9" radius={[4, 4, 0, 0]} name="Visits" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-4 grid gap-6 sm:grid-cols-2">
                        <div>
                            <p className="mb-2 flex items-center gap-1.5 text-xs font-600 uppercase tracking-wide text-zinc-500"><Share2 className="h-3.5 w-3.5" /> Where visitors come from</p>
                            {(traffic.top_sources || []).length ? traffic.top_sources.map((c) => (
                                <div key={c.label} className="flex items-center justify-between py-1 text-sm" data-testid={`traffic-source-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}><span className="truncate pr-2 text-zinc-700">{c.label}</span><span className="font-600">{c.count}</span></div>
                            )) : <p className="text-sm text-zinc-400">No visits recorded yet.</p>}
                        </div>
                        <div>
                            <p className="mb-2 flex items-center gap-1.5 text-xs font-600 uppercase tracking-wide text-zinc-500"><MapPin className="h-3.5 w-3.5" /> Top cities</p>
                            {traffic.top_cities.length ? traffic.top_cities.map((c) => (
                                <div key={c.label} className="flex items-center justify-between py-1 text-sm" data-testid={`traffic-city-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}><span className="truncate pr-2 text-zinc-700">{c.label}</span><span className="font-600">{c.count}</span></div>
                            )) : <p className="text-sm text-zinc-400">No visits recorded yet.</p>}
                        </div>
                        <div>
                            <p className="mb-2 flex items-center gap-1.5 text-xs font-600 uppercase tracking-wide text-zinc-500"><Globe className="h-3.5 w-3.5" /> Top states / regions</p>
                            {traffic.top_states.length ? traffic.top_states.map((c) => (
                                <div key={c.label} className="flex items-center justify-between py-1 text-sm"><span className="truncate pr-2 text-zinc-700">{c.label}</span><span className="font-600">{c.count}</span></div>
                            )) : <p className="text-sm text-zinc-400">No visits recorded yet.</p>}
                        </div>
                    </div>
                </div>
            )}

            <div className="mt-6 flex items-center gap-2" data-testid="analytics-range">
                {RANGES.map((r) => (
                    <button key={r.key} data-testid={`range-${r.key}`} onClick={() => setRange(r.key)}
                        className={`rounded-full px-4 py-1.5 text-sm font-600 transition-colors ${range === r.key ? "bg-zinc-900 text-white" : "border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-100"}`}>
                        {r.label}
                    </button>
                ))}
            </div>

            {isLoading ? (
                <p className="mt-6 text-sm text-zinc-500">Loading analytics…</p>
            ) : (
                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                    <ChartCard title="Revenue by plan" subtitle="New committed MRR from memberships started in each period, split by plan">
                        <AreaChart data={series} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                            <defs>
                                <linearGradient id="stdGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.35} />
                                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="unlGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#059669" stopOpacity={0.35} />
                                    <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <Tooltip formatter={(v) => usd(v)} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Area type="monotone" dataKey="revenue_standard" stackId="rev" stroke="#2563eb" strokeWidth={2} fill="url(#stdGrad)" name="Standard ($20)" />
                            <Area type="monotone" dataKey="revenue_unlimited" stackId="rev" stroke="#059669" strokeWidth={2} fill="url(#unlGrad)" name="Unlimited ($50)" />
                        </AreaChart>
                    </ChartCard>

                    <ChartCard title="New signups" subtitle="New members joining in each period">
                        <BarChart data={series} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <Tooltip />
                            <Bar dataKey="signups" fill="#2563eb" radius={[4, 4, 0, 0]} name="Signups" />
                        </BarChart>
                    </ChartCard>

                    <ChartCard title="Paid memberships" subtitle="New members who activated a paid plan in each period">
                        <BarChart data={series} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <Tooltip />
                            <Bar dataKey="paid" fill="#059669" radius={[4, 4, 0, 0]} name="Paid memberships" />
                        </BarChart>
                    </ChartCard>

                    <ChartCard title="Cancellations" subtitle="Subscriptions canceled in each period">
                        <BarChart data={series} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <Tooltip />
                            <Bar dataKey="cancellations" fill="#ef4444" radius={[4, 4, 0, 0]} name="Cancellations" />
                        </BarChart>
                    </ChartCard>

                    <ChartCard title="Signups vs. cancellations" subtitle="Net growth trend">
                        <LineChart data={series} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Line type="monotone" dataKey="signups" stroke="#2563eb" strokeWidth={2} dot={false} name="Signups" />
                            <Line type="monotone" dataKey="cancellations" stroke="#ef4444" strokeWidth={2} dot={false} name="Cancellations" />
                        </LineChart>
                    </ChartCard>
                </div>
            )}
        </div>
    );
}
